import { ArrowRightIcon } from 'lucide-react'

import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import OpacityTransition from 'components/reusable/OpacityTransition'
import { Button } from 'slab'
import { useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdProofOfQle } from 'utils/web_api'

import ProofOfQLEFileUpload from '../components/ProofOfQLEFileUpload'
import { QLEButtonLayout } from '../components/QLEButtonLayout'
import { useOnboarding } from '../OnboardingContext'
import { type StepComponentProps } from '../types'
import { getQleDocumentUploadCopy } from '../utils'

export function QleDocumentUploadPage ({
  navigateToNextStep,
  navigateToPreviousStep,
  navigateToSpecificStep,
}: StepComponentProps) {
  const { enrollmentEventId, isLoading: isOnboardingLoading, onboardingData } = useOnboarding()

  const { data: proofOfQle, isLoading: isProofOfQleLoading } = useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdProofOfQle(
    enrollmentEventId,
    {
      query: {
        enabled: !!enrollmentEventId,
      },
    }
  )

  const isLoading = isOnboardingLoading || isProofOfQleLoading

  const hasUploadedDocuments = (proofOfQle?.length ?? 0) > 0

  const enrollmentSubtype = onboardingData?.enrollment_event?.subtype

  // Medicare and thatch_incident does not require document upload - this page can be skipped
  // TODO: Remove document upload from the medicare and thatch_incident QLE flows
  const skippableEnrollmentSubtype = enrollmentSubtype === 'medicare' || enrollmentSubtype === 'thatch_incident'
  const nextButtonDisabled = !hasUploadedDocuments && !skippableEnrollmentSubtype
  // copyConfig is undefined if the enrollment subtype is not found
  const copyConfig = getQleDocumentUploadCopy(enrollmentSubtype)

  return (
    <OpacityTransition>
      <div data-testid='QleDocumentUploadPage' className='max-w-[44rem]'>
        <div className='mb-10'>
          <h1 className='heading-lg text-gray-800 mb-3'>
            Upload your supporting documents
          </h1>
          <p className='body text-gray-500 mb-6'>
            In order to make midyear changes to your coverage, we have to include proof of your qualifying life event. That proof <strong>must</strong> have the date of when the event happened somewhere on it.
          </p>

          {copyConfig && (
            <div className='mb-6'>
              <p className='body text-gray-500 mb-3'>
                {copyConfig.introText}
              </p>

              {copyConfig.documentExamples.length > 0 && (
                <ul className='list-disc pl-6 body text-gray-500 mb-3'>
                  {copyConfig.documentExamples.map((example, index) => (
                    <li key={index}>{example}</li>
                  ))}
                </ul>
              )}

              {copyConfig.note && (
                <p className='body text-gray-500 italic'>
                  {copyConfig.note}
                </p>
              )}
            </div>
          )}
        </div>

        <div className='mb-10'>
          {isLoading || !enrollmentEventId
            ? (
              <LoadingSpinnerCentered height='200px' />
              )
            : (
              <ProofOfQLEFileUpload
                proofOfQle={proofOfQle}
                enrollmentEventId={enrollmentEventId}
              />
              )}
        </div>

        <QLEButtonLayout
          backButton={
            <Button
              variant='secondary'
              size='large'
              type='button'
              onClick={() => {
                navigateToPreviousStep?.(onboardingData)
              }}
            >
              Back
            </Button>
          }
          rightButtons={
            <Button
              rightIcon={ArrowRightIcon}
              variant='primary'
              size='large'
              type='button'
              disabled={nextButtonDisabled}
              onClick={() => {
                navigateToNextStep()
              }}
            >
              Next
            </Button>
          }
          navigateToSpecificStep={navigateToSpecificStep}
        />
      </div>
    </OpacityTransition>
  )
}

export default QleDocumentUploadPage
